/**
 * Primitive field schemas reused across entity schemas. Kept in their own
 * module so entity files (challenge.ts, auth.ts) can import them without a
 * circular dependency through the barrel (index.ts).
 */

import { z } from 'zod';
import { isWellFormedLanguoidCode } from '../languoids.js';
import { isValidRegisterLabel } from '../registers.js';

// Validates the *shape* of a languoid code only (e.g. "spa_Latn"). Membership in
// the live catalog is a DB concern checked server-side, so a languoid added
// mid-project validates without a code change. See packages/shared/languoids.ts.
export const languoidCodeSchema = z.string().refine(isWellFormedLanguoidCode, {
  message: 'Languoid code must look like "spa_Latn" (ISO 639-3 + script).',
});

export const registerLabelSchema = z
  .string()
  .length(3)
  .refine(isValidRegisterLabel, { message: 'Invalid register label' });

export const segmentIdSchema = z
  .string()
  .regex(/^S\d{3}$/, { message: 'Segment ID must match S### (e.g. S001)' });

export const difficultyLevelSchema = z.enum(['HARD', 'EXTRA_HARD']);

export const recordingFlowSchema = z.enum(['SOLO', 'COORDINATED']);
export type RecordingFlow = z.infer<typeof recordingFlowSchema>;

/**
 * Cursor-based pagination query (AGENT.md §20.5). Offset paging is never used.
 */
export const paginationQuerySchema = z.object({
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});
export type PaginationQuery = z.infer<typeof paginationQuerySchema>;
